import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { formatPhone, standbyOutlook } from '@jupi/shared'
import { formatDateTime } from '@jupi/ui'
import type { Booking } from '../api/queries'
import { standbyPlaceLabel } from '../features/calendar/standbyPlace'

function row(label: string, value: React.ReactNode) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 96, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ minWidth: 0 }}>
        {value}
      </Typography>
    </Stack>
  )
}

// Whose booking it is and its history: who it is for (with the phone, which
// the API already withholds when the member keeps their details private),
// who made it when that was someone else, and when it was made and last
// changed.
export function BookingDetails({ booking, bookings = [] }: { booking: Booking; bookings?: readonly Booking[] }) {
  const { t } = useTranslation()
  // `updatedAt` moves with every change, including the first save: only
  // call it a change when it is later than the booking was made.
  const changed = new Date(booking.updatedAt).getTime() - new Date(booking.createdAt).getTime() > 1000
  // A standby: what it waits behind, and when it gives up.
  const outlook =
    booking.kind === 'option'
      ? standbyOutlook(bookings, booking.registration, { startsAt: new Date(booking.startsAt), endsAt: new Date(booking.endsAt) }, {
          excludeBookingId: booking.bookingId,
          before: { createdAt: booking.createdAt, bookingId: booking.bookingId },
        })
      : null
  const madeByOther = booking.createdBy !== null && booking.createdBy !== booking.userId

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Stack spacing={0.5}>
        {row(
          t('booking.bookedFor'),
          <>
            {booking.displayName}
            {booking.phone ? (
              <Link
                href={`tel:${booking.phone.replace(/[\s()-]+/g, '')}`}
                underline="hover"
                sx={{ ml: 1, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                <Icon icon="mdi:phone" width={14} />
                {formatPhone(booking.phone)}
              </Link>
            ) : null}
          </>,
        )}
        {madeByOther && booking.createdByName ? row(t('booking.madeBy'), booking.createdByName) : null}
        {outlook
          ? row(
              t('booking.standby.queue'),
              <>
                <strong>{standbyPlaceLabel(t, booking.queuePosition ?? outlook.ahead.length + 1)}</strong>
                {' · '}
                {t('booking.standby.waitingFor', { count: outlook.holding.length })}
              </>,
            )
          : null}
        {outlook
          ? row(
              '',
              booking.expiresAt
                ? t('booking.standby.dropsAt', { when: formatDateTime(booking.expiresAt) })
                : t('booking.standby.waitsUntilOver'),
            )
          : null}
        {row(t('booking.bookedAt'), formatDateTime(booking.createdAt))}
        {changed ? row(t('booking.changedAt'), formatDateTime(booking.updatedAt)) : null}
      </Stack>
    </Box>
  )
}
